import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { Eye, Trash2, Mail, Phone, Circle, CheckCircle } from 'lucide-react';
import { ContactRequest } from '../../services/contactRequestService';

interface ContactRequestsListViewProps {
    requests: ContactRequest[];
    canEdit: boolean;
    onView: (request: ContactRequest) => void;
    onDelete: (id: number) => void;
}

const ContactRequestsListView: React.FC<ContactRequestsListViewProps> = ({
    requests,
    canEdit,
    onView,
    onDelete,
}) => {
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <Badge bg="primary"><Circle size={12} className="me-1" />New</Badge>;
            case 'read':
                return <Badge bg="info"><Eye size={12} className="me-1" />Read</Badge>;
            case 'closed':
                return <Badge bg="success"><CheckCircle size={12} className="me-1" />Closed</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getWhatsAppLink = (phoneNumber: string) => {
        // Remove all non-numeric characters except +
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    return (
        <div className="row g-3">
            {requests.map((request) => (
                <div key={request.id} className="col-12 col-md-6 col-lg-4">
                    <Card className="h-100 border-0 shadow-sm" style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                    >
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="flex-grow-1">
                                    <h5 className="mb-1" style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827' }}>
                                        {request.name} {request.last_name || ''}
                                    </h5>
                                    <small className="text-muted">{formatDate(request.created_at)}</small>
                                </div>
                                {getStatusBadge(request.status)}
                            </div>

                            <div className="mb-3" style={{ minHeight: '120px' }}>
                                <div className="mb-2 small">
                                    <Mail size={14} className="me-2 text-muted" />
                                    <a href={`mailto:${request.email}`} style={{ textDecoration: 'none', color: '#4b5563' }}>
                                        {request.email}
                                    </a>
                                </div>
                                {request.indicative && request.number && (
                                    <div className="mb-2 small">
                                        <Phone size={14} className="me-2 text-muted" />
                                        <a href={getWhatsAppLink(`${request.indicative}${request.number}`)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4b5563' }}>
                                            {request.indicative} {request.number}
                                        </a>
                                    </div>
                                )}
                                {request.subject && (
                                    <div className="mb-2">
                                        <strong className="small">Subject:</strong>
                                        <p className="small text-muted mb-0">{request.subject}</p>
                                    </div>
                                )}
                                {request.message && (
                                    <div className="mb-2">
                                        <p className="small text-muted mb-0" style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                        }}>
                                            {request.message}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                                <Badge bg="secondary" className="small">{request.source || 'unknown'}</Badge>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => onView(request)}
                                    >
                                        <Eye size={14} />
                                    </Button>
                                    {canEdit && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => onDelete(request.id)}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default ContactRequestsListView;
